import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthUser } from '../types';
import { authService } from '../services/auth.service';
import { storageService } from '../utils/storage';

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  hasPermission: (module: string) => boolean;
  /** Action-level check: 'create' | 'read' | 'update' | 'delete' | 'publish' */
  hasAction: (module: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Validate session on app initialization
    validateSession();
  }, []);

  const validateSession = async () => {
    setIsLoading(true);
    
    try {
      // The access token lives in memory only, so after a page reload it is
      // gone. Restore it from the httpOnly refresh cookie before deciding
      // the user is logged out.
      if (!storageService.hasToken()) {
        try {
          await authService.renewToken();
          console.log('🔄 [AuthContext] Session restored from refresh cookie');
        } catch {
          console.log('⚠️ [AuthContext] No active session');
        }
      }

      // Check if user is authenticated
      if (!storageService.hasToken()) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Get stored user and permissions
      let storedUser = storageService.getUser();
      if (!storedUser) {
        try {
          storedUser = await authService.getCurrentUser();
        } catch {
          storedUser = null;
        }
      }
      const storedPermissions = storageService.getPermissions();
      
      console.log('🔄 [AuthContext] Validating session...');
      console.log('👤 [AuthContext] Stored user:', storedUser?.email);
      console.log('🔐 [AuthContext] Stored permissions:', storedPermissions?.length || 0);
      
      if (storedUser) {
        const userPermissions = storedPermissions || [];
        
        let currentUser = storedUser;
        try {
          currentUser = await authService.getCurrentUser();
          console.log('✅ [AuthContext] Token validated successfully');
        } catch (error: any) {
          const isUnauthorized = error?.error?.status === 401 || error?.status === 401;
          if (isUnauthorized) {
            console.error('❌ [AuthContext] Token expired and refresh failed - forcing logout');
            storageService.clearAuth();
            setUser(null);
            setIsLoading(false);
            return;
          }
          console.warn('⚠️ [AuthContext] Token validation failed (non-auth error), using stored user:', error);
        }
        
        console.log('✅ [AuthContext] Using permissions:', userPermissions.length);
        
        // Convert to AuthUser format
        const authUser: AuthUser = {
          id: currentUser.id.toString(),
          username: currentUser.username,
          email: currentUser.email,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          role: {
            id: '1',
            name: currentUser.roleName || 'Admin',
            code: currentUser.roleCode || '',
            description: '',
            permissions: userPermissions,
            isActive: true,
            isSystem: currentUser.roleCode === 'strapi-super-admin',
            usersCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          permissions: userPermissions
        };
        
        console.log('🎯 [AuthContext] Final user object permissions:', authUser.permissions.length);
        setUser(authUser);
      } else {
        console.log('⚠️ [AuthContext] No stored user found');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ [AuthContext] Session validation error:', error);
      // Don't clear auth on every error - only if there's no stored user
      const storedUser = storageService.getUser();
      if (!storedUser) {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe?: boolean): Promise<boolean> => {
    console.log('🔄 [AuthContext] Login started for:', email);
    setIsLoading(true);

    try {
      // Call real API login
      console.log('📞 [AuthContext] Calling authService.login...');
      const response = await authService.login({
        email,
        password,
        rememberMe
      });

      console.log('✅ [AuthContext] Login response received:', response);

      // Convert to AuthUser format
      const authUser: AuthUser = {
        id: response.user.id.toString(),
        username: response.user.username,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        role: {
          id: '1',
          name: response.user.roleName || 'Admin',
          code: response.user.roleCode || '',
          description: '',
          permissions: response.user.permissions || [],
          isActive: true,
          isSystem: response.user.roleCode === 'strapi-super-admin',
          usersCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        permissions: response.user.permissions || []
      };
      
      console.log('👤 [AuthContext] Setting user state:', authUser);
      setUser(authUser);
      setIsLoading(false);
      console.log('✅ [AuthContext] Login completed successfully!');
      return true;
    } catch (error: any) {
      console.error('❌ [AuthContext] Login failed:', error);
      console.error('📋 [AuthContext] Error type:', error.constructor.name);
      console.error('📋 [AuthContext] Error message:', error.message);
      setIsLoading(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      setUser(null);
      // Revokes the server session and clears the httpOnly refresh cookie,
      // then clears local state and redirects to /login.
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear and redirect even if there's an error
      storageService.clearAuth();
      window.location.href = '/login';
    }
  };

  const hasPermission = (module: string): boolean => {
    if (!user) {
      console.log('❌ [AuthContext] hasPermission check failed: No user');
      return false;
    }
    const hasAccess = user.permissions.includes(module);
    console.log(`🔍 [AuthContext] Checking permission for "${module}":`, hasAccess ? '✅ Allowed' : '❌ Denied');
    return hasAccess;
  };

  const hasAction = (module: string, action: string): boolean => {
    if (!user) return false;
    return (
      user.permissions.includes(module) &&
      user.permissions.includes(`${module}:${action}`)
    );
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    hasPermission,
    hasAction
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
